import { useEffect, useState } from "react";
import { Modal, Input, InputNumber } from "antd";
import {
  CloseOutlined,
  CreditCardOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles } from "../../../../utils/modalStyles";
import { useUnsavedChangesGuard } from "../panel/useUnsavedChangesGuard";
import {
  isSystemPaymentMethod,
  type PaymentMethod,
  type PaymentMethodCreatePayload,
  type PaymentMethodUpdatePayload,
} from "../../../../api/paymentMethods";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  // Плътни акцентни фонове — еднакви в двете теми.
  brand: "#D97706",
  brandDeep: "#B45309",
  brandTint: "var(--color-brand-tint)",
};

interface Props {
  open: boolean;
  mode: "create" | "edit";
  paymentMethod?: PaymentMethod | null;
  onSubmit: (
    data: PaymentMethodCreatePayload | PaymentMethodUpdatePayload,
  ) => Promise<void>;
  onCancel: () => void;
}

const MAX_LEN = 50;

const PaymentMethodModal = ({
  open,
  mode,
  paymentMethod,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();

  const [nameBg, setNameBg] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [sortIndex, setSortIndex] = useState<number | null>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Пазач за незапазени промени — пита при затваряне, ако нещо е пипано.
  const { requestClose, markPristine, unsavedDialog } = useUnsavedChangesGuard({
    open,
    snapshot: JSON.stringify({ nameBg, nameEn, sortIndex }),
    isSubmitting,
    onClose: onCancel,
  });

  const isEdit = mode === "edit";
  const isProtected =
    isEdit && !!paymentMethod && isSystemPaymentMethod(paymentMethod.id);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && paymentMethod) {
      setNameBg(paymentMethod.name_bg);
      setNameEn(paymentMethod.name_en);
      setSortIndex(paymentMethod.sort_index ?? 0);
    } else {
      setNameBg("");
      setNameEn("");
      setSortIndex(0);
    }
    markPristine();
  }, [open, mode, paymentMethod, markPristine]);

  const trimmedBg = nameBg.trim();
  const trimmedEn = nameEn.trim();
  // Празно поле се пази в state като null, но при save се третира като 0.
  const effectiveSortIndex = sortIndex ?? 0;
  const sortIndexValid =
    Number.isInteger(effectiveSortIndex) && effectiveSortIndex >= 0;
  const namesValid =
    trimmedBg.length > 0 &&
    trimmedEn.length > 0 &&
    trimmedBg.length <= MAX_LEN &&
    trimmedEn.length <= MAX_LEN;
  // За protected методи в режим edit името не се валидира — то е read-only.
  const canSave =
    sortIndexValid && (isProtected ? true : namesValid) && !isSubmitting;

  const handleSave = async () => {
    if (!canSave) return;
    setIsSubmitting(true);
    try {
      if (isEdit && isProtected) {
        // Protected методи: пращай само sort_index — иначе бекендът връща 403.
        await onSubmit({ sort_index: effectiveSortIndex });
      } else {
        await onSubmit({
          name_bg: trimmedBg,
          name_en: trimmedEn,
          sort_index: effectiveSortIndex,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalWidth = isMobile ? undefined : isTablet ? 420 : 460;

  return (
    <Modal
      open={open}
      onCancel={requestClose}
      // Esc го поема пазачът за незапазени промени (useUnsavedChangesGuard).
      keyboard={false}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      {unsavedDialog}
      <style>{`
        @keyframes rspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .pm-modal .ant-input {
          border-radius: 10px !important;
          border-color: ${R.border} !important;
          min-height: 42px !important;
          height: 42px !important;
          padding: 4px 12px !important;
          background: ${R.surface} !important;
          font-size: 13px;
          color: ${R.ink};
          transition: border-color 120ms, box-shadow 120ms;
        }
        .pm-modal .ant-input:hover,
        .pm-modal .ant-input:focus {
          border-color: ${R.brand} !important;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
        }
        .pm-modal .ant-input::placeholder { color: ${R.textMuted} !important; }
        .pm-modal .ant-input[disabled],
        .pm-modal .ant-input-disabled {
          background: ${R.bg} !important;
          color: ${R.textSoft} !important;
          cursor: not-allowed !important;
        }
        .pm-modal .ant-input-number {
          width: 100% !important;
          border-radius: 10px !important;
          border-color: ${R.border} !important;
          background: ${R.surface} !important;
          transition: border-color 120ms, box-shadow 120ms;
        }
        .pm-modal .ant-input-number .ant-input-number-input {
          height: 40px !important;
          padding: 4px 12px !important;
          font-size: 13px;
          color: ${R.ink};
        }
        .pm-modal .ant-input-number:hover,
        .pm-modal .ant-input-number-focused {
          border-color: ${R.brand} !important;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
        }
      `}</style>

      <div
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isNarrow ? "14px 16px" : "20px 24px",
            borderBottom: `1px solid ${R.border}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            flexShrink: 0,
            background: R.surface,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: R.brandTint,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--color-brand-deep-ink)",
              flexShrink: 0,
            }}
          >
            {isEdit ? (
              <EditOutlined style={{ fontSize: 18 }} />
            ) : (
              <CreditCardOutlined style={{ fontSize: 18 }} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: isNarrow ? 16 : 17,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: R.ink,
                lineHeight: 1.25,
              }}
            >
              {isEdit
                ? t("paymentMethodsConfig.editTitle")
                : t("paymentMethodsConfig.createTitle")}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: R.textSoft,
                lineHeight: 1.4,
                marginTop: 4,
              }}
            >
              {isEdit
                ? t("paymentMethodsConfig.editSubtitle")
                : t("paymentMethodsConfig.createSubtitle")}
            </div>
          </div>
          <button
            onClick={requestClose}
            aria-label="Close"
            style={{
              height: 30,
              width: 30,
              borderRadius: 6,
              background: "transparent",
              border: "none",
              color: R.textMuted,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 120ms, color 120ms",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
              e.currentTarget.style.color = R.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = R.textMuted;
            }}
          >
            <CloseOutlined style={{ fontSize: 14 }} />
          </button>
        </div>

        {/* Body */}
        <div
          className="pm-modal"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: isNarrow ? "14px 16px" : "18px 22px",
            background: R.bg,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          <Field label={t("paymentMethodsConfig.fieldNameBg")}>
            <Input
              placeholder={t("paymentMethodsConfig.fieldNameBgPlaceholder")}
              value={nameBg}
              maxLength={MAX_LEN}
              disabled={isProtected}
              onChange={(e) => setNameBg(e.target.value)}
              onPressEnter={handleSave}
            />
          </Field>
          <Field label={t("paymentMethodsConfig.fieldNameEn")}>
            <Input
              placeholder={t("paymentMethodsConfig.fieldNameEnPlaceholder")}
              value={nameEn}
              maxLength={MAX_LEN}
              disabled={isProtected}
              onChange={(e) => setNameEn(e.target.value)}
              onPressEnter={handleSave}
            />
          </Field>
          <Field label={t("paymentMethodsConfig.fieldSortIndex")}>
            <InputNumber
              min={0}
              step={1}
              precision={0}
              value={sortIndex}
              onChange={(v) => {
                if (v === null || v === undefined) {
                  setSortIndex(null);
                  return;
                }
                if (typeof v === "number" && Number.isFinite(v) && v >= 0) {
                  setSortIndex(Math.floor(v));
                }
              }}
              onBlur={() => {
                if (sortIndex === null) setSortIndex(0);
              }}
              onPressEnter={handleSave}
            />
          </Field>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: `1px solid ${R.border}`,
            padding: isNarrow ? "12px 16px" : "14px 22px",
            display: "flex",
            flexDirection: "row",
            gap: 10,
            flexShrink: 0,
            background: R.surface,
          }}
        >
          <button
            type="button"
            onClick={requestClose}
            disabled={isSubmitting}
            style={{
              height: 42,
              padding: "0 18px",
              borderRadius: 10,
              border: `1px solid ${R.border}`,
              background: "transparent",
              color: R.textSoft,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              transition: "background 120ms",
              flex: 1,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting)
                e.currentTarget.style.background = "var(--color-shade-1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            style={{
              height: 42,
              padding: "0 22px",
              borderRadius: 10,
              border: "none",
              background: canSave ? R.brand : "rgba(217,119,6,0.35)",
              color: "#fff",
              fontSize: 13.5,
              fontWeight: 700,
              letterSpacing: -0.2,
              cursor: canSave ? "pointer" : "not-allowed",
              transition: "background 120ms",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              flex: 1,
            }}
            onMouseEnter={(e) => {
              if (canSave) e.currentTarget.style.background = R.brandDeep;
            }}
            onMouseLeave={(e) => {
              if (canSave) e.currentTarget.style.background = R.brand;
            }}
          >
            {isSubmitting && (
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: "2px solid rgba(255,255,255,0.5)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "rspin 900ms linear infinite",
                }}
              />
            )}
            {isEdit
              ? t("paymentMethodsConfig.save")
              : t("paymentMethodsConfig.create")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: R.surface,
        border: `1px solid ${R.border}`,
        borderRadius: 12,
        padding: "14px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          color: R.textMuted,
          letterSpacing: 1.2,
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

export default PaymentMethodModal;
